import Modal from "components/Modals/Modal";
import React, { useCallback, useRef, useState } from "react";
import { TrashIcon } from "@heroicons/react/24/solid";
import CopyButton from "components/Graphs/CopyButton";
import { InputField } from "components/UI-lib";
import Tooltip from "@mui/material/Tooltip";

const TOOLTIP_SLOT_PROPS = {
  popper: { disablePortal: true },
} as const;

const DEFAULT_ENVIRONMENT_BULLETS = (
  <>
    <li>All compute and storage charges stop</li>
    <li>
      Your data will be{" "}
      <strong>permanently deleted and cannot be recovered</strong>
    </li>
    <li>
      Move any data you want to keep <strong>before</strong> deleting
    </li>
  </>
);

export interface DeleteConfirmModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onDelete?: () => void;
  resourceName: string;
  resourceLabel?: string;
  title?: string;
  bullets?: React.ReactNode;
  orgName?: string;
  ownerName?: string;
  ownerLoading?: boolean;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  show,
  setShow,
  onDelete = () => {},
  resourceName,
  resourceLabel = "Environment",
  title,
  bullets = DEFAULT_ENVIRONMENT_BULLETS,
  orgName,
  ownerName,
  ownerLoading = false,
}) => {
  const [deleteInput, setDeleteInput] = useState("");
  const deleteInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setDeleteInput("");
  }, []);

  const expectedConfirmation = resourceName.trim();
  const isConfirmEnabled =
    expectedConfirmation.length > 0 &&
    deleteInput.trim() === expectedConfirmation;

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;

    onDelete();
    resetState();
    setShow(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isConfirmEnabled) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Modal
      isOpen={show}
      setOpen={setShow}
      onClose={() => {
        resetState();
        setShow(false);
      }}
      title=""
      hideIcon
      initialFocus={deleteInputRef}
      body={
        <div>
          <div className="flex items-center gap-2 mb-4">
            <TrashIcon className="h-5 w-5 text-white" />
            <h3 className="text-lg font-medium text-primary">
              {title ?? `Permanently Delete ${resourceLabel} and Data`}
            </h3>
          </div>
          <ul className="list-disc pl-5 text-sm space-y-1 text-secondary">
            {bullets}
          </ul>
          {(resourceName || orgName || ownerName || ownerLoading) && (
            <div className="mt-4 rounded-md border border-zinc-800 bg-secondaryBg/40 p-3 text-sm text-secondary">
              {resourceName && (
                <div className="flex items-center gap-8 py-0.5">
                  <span className="text-secondary shrink-0">
                    {resourceLabel}
                  </span>
                  <Tooltip
                    title={resourceName}
                    placement="bottom"
                    arrow
                    slotProps={TOOLTIP_SLOT_PROPS}
                  >
                    <span className="font-mono text-primary truncate min-w-0 flex-1 text-right">
                      {resourceName}
                    </span>
                  </Tooltip>
                </div>
              )}
              {orgName && (
                <div className="flex items-center gap-8 py-0.5">
                  <span className="text-secondary shrink-0">Organization</span>
                  <Tooltip
                    title={orgName}
                    placement="bottom"
                    arrow
                    slotProps={TOOLTIP_SLOT_PROPS}
                  >
                    <span className="text-primary truncate min-w-0 flex-1 text-right">
                      {orgName}
                    </span>
                  </Tooltip>
                </div>
              )}
              {(ownerName || ownerLoading) && (
                <div className="flex items-center gap-8 py-0.5">
                  <span className="text-secondary shrink-0">Owner</span>
                  {ownerName ? (
                    <Tooltip
                      title={ownerName}
                      placement="bottom"
                      arrow
                      slotProps={TOOLTIP_SLOT_PROPS}
                    >
                      <span className="text-primary truncate min-w-0 flex-1 text-right">
                        {ownerName}
                      </span>
                    </Tooltip>
                  ) : (
                    <span
                      aria-label="Loading owner"
                      className="ml-auto h-4 w-32 rounded bg-zinc-700 animate-pulse"
                    />
                  )}
                </div>
              )}
            </div>
          )}
          <div className="mt-4 w-full" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
              Type{" "}
              <span className="font-mono text-primary">
                {expectedConfirmation}
              </span>
              <CopyButton
                value={expectedConfirmation}
                displayValue={<></>}
                className="!inline-flex !gap-0 align-middle mx-1"
              />
              to confirm{" "}
              <span className="text-red-500">
                your data will be permanently deleted
              </span>
            </div>
            <InputField
              hideLabel
              label=""
              placeholder={expectedConfirmation}
              value={deleteInput}
              ref={deleteInputRef}
              onChange={setDeleteInput}
              onKeyDown={handleInputKeyDown}
              errorMessage=""
              data-testid="delete-confirmation-input"
            />
          </div>
        </div>
      }
      cancelLabel="Cancel"
      confirmLabel="Delete"
      disableSuccess={!isConfirmEnabled}
      onSuccess={handleConfirm}
    />
  );
};
